/**
 * The presentation dictionary: how backend vocabularies are worded on screen.
 *
 * One place, so a label cannot drift between the card, the panel and an export.
 * The old frontend carried the decision labels in six.
 *
 * Every map is keyed by a generated union, so removing or renaming a backend
 * value breaks the build here instead of rendering a blank.
 */
import type { MissingEvidenceKey, Priority, Recommendation, RiskKey, SignalKey, Stage } from '../../api/types'

export const STAGE_LABEL: Record<Stage, string> = {
  no_opportunity: 'No opportunity',
  early_exploration: 'Early exploration',
  active_scoping: 'Active scoping',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  qualified: 'Qualified',
  blocked: 'Blocked',
}

export const PRIORITY_LABEL: Record<Priority, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

/**
 * What is still unconfirmed, worded as the thing to go and get.
 *
 * Noun phrases, not sentences: these render as a short list under the verdict and
 * a seller reads them as a to-do. `decision_maker` is not the transcript signal —
 * it is satisfied by the user's own confirmation too, which is why it is not
 * worded "stated decision authority".
 */
export const MISSING_EVIDENCE_LABEL: Record<MissingEvidenceKey, string> = {
  problem_confirmed: 'Confirmed problem',
  operational_impact: 'Operational impact',
  decision_maker: 'Decision maker',
  budget_allocated: 'Budget',
  buying_process_active: 'Buying process',
}

export const RECOMMENDATION_LABEL: Record<Recommendation, string> = {
  keep_investing: 'Keep investing',
  pause: 'Pause',
  stop_investing: 'Stop investing',
}

/**
 * The same three values as an ACT rather than a verdict.
 *
 * "Keep investing" is the right words for a line that reports a decision, and
 * the wrong words for a button in a row of three: at card width the long labels
 * wrap and the row stops looking like one control. Two maps, one vocabulary —
 * both keyed by the generated union, so a new decision value breaks the build
 * in both places.
 */
export const DECISION_ACTION_LABEL: Record<Recommendation, string> = {
  keep_investing: 'Keep',
  pause: 'Pause',
  stop_investing: 'Stop',
}

/** The seven qualification signals, in the order the product reads them. */
export const QUALIFICATION_SIGNALS: readonly SignalKey[] = [
  'problem_confirmed',
  'operational_impact',
  'budget_allocated',
  'decision_authority_present',
  'buying_process_active',
  'internal_momentum',
  'next_step_defined',
]

/** The five late-stage commercial-progression signals, shown after the seven. */
export const PROGRESSION_SIGNALS: readonly SignalKey[] = [
  'solution_presented',
  'pricing_discussed',
  'commercial_objection',
  'contract_engaged',
  'timeline_committed',
]

export const SIGNAL_LABEL: Record<SignalKey, string> = {
  problem_confirmed: 'problem',
  operational_impact: 'impact',
  budget_allocated: 'budget',
  decision_authority_present: 'authority',
  buying_process_active: 'process',
  internal_momentum: 'momentum',
  next_step_defined: 'next step',
  solution_presented: 'solution presented',
  pricing_discussed: 'pricing discussed',
  commercial_objection: 'commercial objection',
  contract_engaged: 'contract engaged',
  timeline_committed: 'timeline committed',
}

export const RISK_LABEL: Record<RiskKey, string> = {
  vendor_saturation: 'Vendor competition',
  vendor_selected: 'Vendor selected',
  implementation_skepticism: 'Implementation skepticism',
  direction_locked: 'Direction locked',
  internal_bias: 'Internal bias',
}
